import React from "react";
import cx from "classnames";
import "./styles.css";
import { CheckCircle, Maximize2, Star } from "react-feather";

export function ImageThumbnail({
  id,
  title,
  url,
  minetype,
  rating,
  isUsed,
  isSelected,
  selectedOrder,
  isUploading,
  onSelect,
  onViewerSelection,
  className,
  size = "default",
  customSize,
  currentSelection,
  defaultAspectRatio,
  hideSelected,
}: {
  id: number;
  title: string;
  url: string;
  minetype: string;
  rating?: number;
  isUsed?: boolean;
  isSelected?: boolean;
  selectedOrder?: number | false;
  isUploading?: boolean;
  onSelect: (event?: React.MouseEvent) => void;
  onViewerSelection?: () => void;
  className?: string;
  size?: "small" | "default" | "large" | "custom";
  customSize?: string;
  currentSelection?: boolean;
  defaultAspectRatio?: boolean;
  hideSelected?: boolean;
}) {
  const isVideo = minetype.startsWith("video");

  const sizeClasses = {
    small: "w-14 h-14",
    default: "w-20 h-20",
    large: "w-28 h-28",
  };

  return (
    <div
      className={cx(
        "group thumbnail relative tooltip tooltip-accent cursor-pointer",
        size !== "custom" && sizeClasses[size],
        defaultAspectRatio &&
          "bg-base-200 w-full h-full justify-center content-center p-3 rounded-lg",
        isUploading && " opacity-50",
        className
      )}
      data-tip={title}
      key={id}
    >
      {onViewerSelection && (
        <span
          onClick={onViewerSelection}
          className="absolute badge badge-accent top-2 left-2 z-10 invisible group-hover:visible"
        >
          <Maximize2 size={8} />
        </span>
      )}

      {!defaultAspectRatio && rating && (
        <span
          className={cx(
            "absolute top-2 left-2 z-10 group-hover:invisible badge badge-neutral text-primary text-xs",
            size === "small" && "badge-xs",
            size === "large" && "badge-lg"
          )}
        >
          {rating + ""}
          &nbsp;
          <Star size={size === "small" ? 8 : 12} />
        </span>
      )}

      {!defaultAspectRatio && isUsed && (
        <span
          className={cx(
            "absolute bottom-2 left-2 z-10 group-hover:invisible badge badge-success text-white text-xs",
            size === "small" && "badge-xs",
            size === "large" && "badge-lg"
          )}
        >
          <CheckCircle size={12} />
        </span>
      )}

      {!defaultAspectRatio && isSelected && (
        <span
          className={cx(
            "absolute badge badge-primary top-2 right-2 z-10",
            size === "small" && "badge-xs",
            size === "large" && "badge-lg"
          )}
        >
          {selectedOrder}
        </span>
      )}
      <div
        className={cx(
          "flex items-center justify-center overflow-hidden",
          defaultAspectRatio
            ? "content-fit max-h-full"
            : "absolute w-full h-full rounded-lg",
          (currentSelection || isSelected) && !hideSelected
            ? "border-4 border-primary"
            : "hover:bg-contain",
          isUsed && " opacity-40 hover:opacity-100"
        )}
        onClick={(event) => onSelect(event)}
      >
        {isUploading && (
          <span className="absolute loading loading-spinner loading-lg bg-primary"></span>
        )}

        {!isVideo && (
          <img
            src={url}
            alt=""
            className={cx(
              "w-full h-full",
              defaultAspectRatio ? "object-contain" : "object-fill",
              isSelected ? "" : "hover:object-contain"
            )}
            style={size === "custom" ? { maxHeight: customSize } : {}}
          />
        )}

        {isVideo && (
          <video
            src={url}
            className={cx(
              "w-full h-full",
              defaultAspectRatio ? "object-contain" : "object-fill",
              isSelected ? "" : "hover:object-contain"
            )}
          />
        )}
      </div>

      <div className="w-full flex items-center justify-center mt-2 gap-2">
        {defaultAspectRatio && !hideSelected && isSelected && (
          <span
            className={cx(
              "badge badge-primary",
              size === "small" && "badge-xs",
              size === "large" && "badge-lg"
            )}
          >
            {selectedOrder}
          </span>
        )}

        {defaultAspectRatio && rating && !hideSelected && (
          <span
            className={cx(
              "badge badge-neutral text-primary text-xs",
              size === "small" && "badge-xs",
              size === "large" && "badge-lg"
            )}
          >
            {rating + ""}
            &nbsp;
            <Star size={size === "small" ? 8 : 12} />
          </span>
        )}

        {defaultAspectRatio && isUsed && !hideSelected && (
          <span
            className={cx(
              "badge badge-success text-white text-xs",
              size === "small" && "badge-xs",
              size === "large" && "badge-lg"
            )}
          >
            <CheckCircle size={12} />
          </span>
        )}
      </div>
    </div>
  );
}
